if (!customElements.get('lp-image-crop-modal')) {
  customElements.define(
    'lp-image-crop-modal',
    class LpImageCropModal extends HTMLElement {
      /** @type {HTMLCanvasElement} */
      canvas = null;

      /** @type {CanvasRenderingContext2D} */
      ctx = null;

      /** @type {HTMLImageElement} */
      image = null;

      /** @type {{ x: number, y: number, w: number, h: number }} */
      cropBox = { x: 0, y: 0, w: 0, h: 0 };

      /** @type {{ x: number, y: number, w: number, h: number }} */
      imageBounds = { x: 0, y: 0, w: 0, h: 0 };

      /** @type {string|null} */
      activeHandle = null;

      /** @type {{ x: number, y: number, box: object }|null} */
      dragStart = null;

      /** @type {string} */
      originalFileName = '';

      /** @type {boolean} */
      isCropConfirming = false;

      connectedCallback() {
        this.modal = this.querySelector('.lp-crop-modal');
        this.canvas = this.querySelector('.lp-crop-modal__canvas');
        this.ctx = this.canvas.getContext('2d');
        this.cropRegion = this.querySelector('[data-ref="crop-region"]');
        this.cropBoxEl = this.querySelector('.lp-crop-modal__crop-box');

        this.bindEvents();
      }

      disconnectedCallback() {
        this.unbindEvents();
      }

      bindEvents() {
        this.onFileChange = this.handleFileChange.bind(this);
        this.onClose = this.close.bind(this);
        this.onConfirm = this.handleConfirm.bind(this);
        this.onBackdropClick = this.handleBackdropClick.bind(this);
        this.onMouseDown = this.handleMouseDown.bind(this);
        this.onMouseMove = this.handleMouseMove.bind(this);
        this.onMouseUp = this.handleMouseUp.bind(this);
        this.onTouchStart = this.handleTouchStart.bind(this);
        this.onTouchMove = this.handleTouchMove.bind(this);
        this.onTouchEnd = this.handleMouseUp.bind(this);
        this.onKeyDown = this.handleKeyDown.bind(this);

        const fileInput = this.closest('.lp-pdp__form-wrapper')?.querySelector('#lp-engraving-photo');
        if (fileInput) {
          fileInput.addEventListener('change', this.onFileChange);
        }

        this.querySelector('.lp-crop-modal__close')?.addEventListener('click', this.onClose);
        this.querySelector('.lp-crop-modal__cancel')?.addEventListener('click', this.onClose);
        this.querySelector('.lp-crop-modal__confirm')?.addEventListener('click', this.onConfirm);
        this.querySelector('.lp-crop-modal__backdrop')?.addEventListener('click', this.onBackdropClick);

        this.cropBoxEl.addEventListener('mousedown', this.onMouseDown);
        this.cropBoxEl.addEventListener('mousemove', this.onMouseMove);
        this.cropBoxEl.addEventListener('mouseup', this.onMouseUp);
        this.cropBoxEl.addEventListener('mouseleave', this.onMouseUp);
        this.cropBoxEl.addEventListener('touchstart', this.onTouchStart, { passive: false });
        this.cropBoxEl.addEventListener('touchmove', this.onTouchMove, { passive: false });
        this.cropBoxEl.addEventListener('touchend', this.onTouchEnd);
      }

      unbindEvents() {
        const fileInput = this.closest('.lp-pdp__form-wrapper')?.querySelector('#lp-engraving-photo');
        if (fileInput) {
          fileInput.removeEventListener('change', this.onFileChange);
        }
      }

      /**
       * @param {Event} e
       */
      handleFileChange(e) {
        if (this.isCropConfirming) return;
        const file = e.target.files?.[0];
        if (!file) return;

        this.originalFileName = file.name;
        e.target.value = '';

        const reader = new FileReader();
        reader.onload = (evt) => {
          const img = new Image();
          img.onload = () => {
            this.image = img;
            this.open();
            this.setupCanvas();
          };
          img.src = evt.target.result;
        };
        reader.readAsDataURL(file);
      }

      open() {
        this.modal.removeAttribute('hidden');
        document.body.style.overflow = 'hidden';
        document.addEventListener('keydown', this.onKeyDown);
      }

      close() {
        this.modal.setAttribute('hidden', '');
        document.body.style.overflow = '';
        document.removeEventListener('keydown', this.onKeyDown);
        this.image = null;
      }

      /**
       * @param {MouseEvent} e
       */
      handleBackdropClick(e) {
        if (e.target === this.querySelector('.lp-crop-modal__backdrop')) {
          this.close();
        }
      }

      /**
       * @param {KeyboardEvent} e
       */
      handleKeyDown(e) {
        if (e.key === 'Escape') this.close();
      }

      setupCanvas() {
        const wrapper = this.querySelector('.lp-crop-modal__canvas-wrapper');
        const maxW = wrapper.clientWidth;
        const maxH = Math.min(window.innerHeight * 0.55, 500);

        const scale = Math.min(maxW / this.image.naturalWidth, maxH / this.image.naturalHeight);
        const drawW = Math.floor(this.image.naturalWidth * scale);
        const drawH = Math.floor(this.image.naturalHeight * scale);

        this.canvas.width = drawW;
        this.canvas.height = drawH;
        this.canvas.style.width = `${drawW}px`;
        this.canvas.style.height = `${drawH}px`;

        this.cropBoxEl.style.width = `${drawW}px`;
        this.cropBoxEl.style.height = `${drawH}px`;

        this.imageBounds = { x: 0, y: 0, w: drawW, h: drawH };

        this.ctx.drawImage(this.image, 0, 0, drawW, drawH);

        const padding = Math.round(Math.min(drawW, drawH) * 0.1);
        this.cropBox = {
          x: padding,
          y: padding,
          w: drawW - padding * 2,
          h: drawH - padding * 2,
        };

        this.renderCropUI();
      }

      renderCropUI() {
        const { x, y, w, h } = this.cropBox;
        const { w: iw, h: ih } = this.imageBounds;

        this.cropRegion.style.left = `${x}px`;
        this.cropRegion.style.top = `${y}px`;
        this.cropRegion.style.width = `${w}px`;
        this.cropRegion.style.height = `${h}px`;

        const overlayTop = this.querySelector('.lp-crop-modal__crop-overlay--top');
        const overlayBottom = this.querySelector('.lp-crop-modal__crop-overlay--bottom');
        const overlayLeft = this.querySelector('.lp-crop-modal__crop-overlay--left');
        const overlayRight = this.querySelector('.lp-crop-modal__crop-overlay--right');

        overlayTop.style.cssText = `top:0;left:0;width:${iw}px;height:${y}px;`;
        overlayBottom.style.cssText = `top:${y + h}px;left:0;width:${iw}px;height:${ih - y - h}px;`;
        overlayLeft.style.cssText = `top:${y}px;left:0;width:${x}px;height:${h}px;`;
        overlayRight.style.cssText = `top:${y}px;left:${x + w}px;width:${iw - x - w}px;height:${h}px;`;
      }

      getPointer(e) {
        const rect = this.cropBoxEl.getBoundingClientRect();
        const touch = e.touches?.[0] ?? e;
        return {
          x: touch.clientX - rect.left,
          y: touch.clientY - rect.top,
        };
      }

      /**
       * @param {MouseEvent|TouchEvent} e
       */
      handleMouseDown(e) {
        e.preventDefault();
        const pt = this.getPointer(e);
        const handle = e.target.closest('[data-handle]')?.dataset?.handle ?? null;

        this.activeHandle = handle;
        this.dragStart = { x: pt.x, y: pt.y, box: { ...this.cropBox } };

        if (!handle) {
          const { x, y, w, h } = this.cropBox;
          if (pt.x >= x && pt.x <= x + w && pt.y >= y && pt.y <= y + h) {
            this.activeHandle = 'move';
          }
        }
      }

      /**
       * @param {MouseEvent|TouchEvent} e
       */
      handleMouseMove(e) {
        if (!this.dragStart) return;
        e.preventDefault();
        const pt = this.getPointer(e);
        const dx = pt.x - this.dragStart.x;
        const dy = pt.y - this.dragStart.y;
        const { x: ox, y: oy, w: ow, h: oh } = this.dragStart.box;
        const { w: iw, h: ih } = this.imageBounds;
        const minSize = 40;

        let { x, y, w, h } = this.cropBox;

        if (this.activeHandle === 'move') {
          x = Math.max(0, Math.min(iw - ow, ox + dx));
          y = Math.max(0, Math.min(ih - oh, oy + dy));
          w = ow;
          h = oh;
        } else if (this.activeHandle === 'se') {
          w = Math.max(minSize, Math.min(iw - ox, ow + dx));
          h = Math.max(minSize, Math.min(ih - oy, oh + dy));
          x = ox;
          y = oy;
        } else if (this.activeHandle === 'sw') {
          x = Math.max(0, Math.min(ox + ow - minSize, ox + dx));
          w = ox + ow - x;
          h = Math.max(minSize, Math.min(ih - oy, oh + dy));
          y = oy;
        } else if (this.activeHandle === 'ne') {
          w = Math.max(minSize, Math.min(iw - ox, ow + dx));
          y = Math.max(0, Math.min(oy + oh - minSize, oy + dy));
          h = oy + oh - y;
          x = ox;
        } else if (this.activeHandle === 'nw') {
          x = Math.max(0, Math.min(ox + ow - minSize, ox + dx));
          w = ox + ow - x;
          y = Math.max(0, Math.min(oy + oh - minSize, oy + dy));
          h = oy + oh - y;
        }

        this.cropBox = { x, y, w, h };
        this.renderCropUI();
      }

      handleMouseUp() {
        this.dragStart = null;
        this.activeHandle = null;
      }

      /**
       * @param {TouchEvent} e
       */
      handleTouchStart(e) {
        this.handleMouseDown(e);
      }

      /**
       * @param {TouchEvent} e
       */
      handleTouchMove(e) {
        this.handleMouseMove(e);
      }

      handleConfirm() {
        if (!this.image) return;

        const { x, y, w, h } = this.cropBox;
        const { w: canvasW, h: canvasH } = this.imageBounds;
        const natW = this.image.naturalWidth;
        const natH = this.image.naturalHeight;

        const scaleX = natW / canvasW;
        const scaleY = natH / canvasH;

        const srcX = x * scaleX;
        const srcY = y * scaleY;
        const srcW = w * scaleX;
        const srcH = h * scaleY;

        const outputCanvas = document.createElement('canvas');
        outputCanvas.width = Math.round(srcW);
        outputCanvas.height = Math.round(srcH);
        const outputCtx = outputCanvas.getContext('2d');
        outputCtx.drawImage(this.image, srcX, srcY, srcW, srcH, 0, 0, outputCanvas.width, outputCanvas.height);

        outputCanvas.toBlob((blob) => {
          const croppedFile = new File([blob], this.originalFileName, { type: 'image/jpeg' });

          const fileInput = this.closest('.lp-pdp__form-wrapper')?.querySelector('#lp-engraving-photo');
          if (fileInput) {
            this.isCropConfirming = true;
            const dataTransfer = new DataTransfer();
            dataTransfer.items.add(croppedFile);
            fileInput.files = dataTransfer.files;
            fileInput.dispatchEvent(new Event('change', { bubbles: true }));
            this.isCropConfirming = false;
          }

          this.close();
        }, 'image/jpeg', 0.92);
      }
    }
  );
}
